import React, { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiBarChart2, FiGrid, FiSettings, FiChevronRight } from 'react-icons/fi'; // bottom-nav + row affordance
import { GiGolfTee } from 'react-icons/gi'; // bottom-nav only
// Menu icons use Phosphor's duotone weight: each glyph is two-tone and picks up
// its item's accent colour (via currentColor), giving the flat line icons depth
// inside the tinted tiles.
import {
  Golf, Trophy, Medal, ShieldChevron, Flag, Gauge, Wrench, CloudArrowDown,
} from '@phosphor-icons/react';
import './Setup.css';
import BottomNav from './BottomNav';
import MainMenu from './MainMenu';
import { useAuth } from './AuthContext';
import { Permission } from './permissions';
import PageHeader from './components/PageHeader';

// ─── Role / capability helpers ─────────────────────────────────────────────────
// Exported because SocietyHub had its own copy of this, and the copy was missing
// the SYS_ADMIN line — so a system admin opening a society they are not a member
// of got a member-level Society hub while the Manage screen beside it gave them
// everything. One definition, one answer.
export const getEffectiveRole = (userProfile, currentSociety) => {
  if (userProfile?.is_system_admin) return 'SYS_ADMIN';
  if (!currentSociety || !userProfile) return 'MEMBER';
  const societyData = userProfile.societies?.find(
    s => String(s.id) === String(currentSociety.id)
  );
  return societyData?.role || 'MEMBER';
};

export const getUserCapabilities = (role) => ({
  canViewLiveEvent: true,
  // Every society member can view the player roster and member list (read-only);
  // only admins+ can manage them (gated inside each page).
  canViewPlayers: true,
  canViewMembers: true,
  canCreateEvents: role !== 'MEMBER',
  canManageSocieties: role !== 'MEMBER',
  canManagePlayers: role !== 'MEMBER',
  canManageCourses: role !== 'MEMBER',
  canManageHandicap: role !== 'MEMBER',
  canManageOOM: role !== 'MEMBER',
  canAccessSystemAdmin: role === 'SYS_ADMIN',
});

// ─── Component ─────────────────────────────────────────────────────────────────
const ControlCentre = () => {
  const {
    currentSociety, userProfile, hasPermission, isSocietyOwner, canCreateRound,
  } = useAuth();

  const role = useMemo(
    () => getEffectiveRole(userProfile, currentSociety),
    [userProfile, currentSociety]
  );
  const caps = useMemo(() => getUserCapabilities(role), [role]);
  // Competition setup lands in the full wizard, which bounces anyone without
  // CREATE_TOURNAMENT straight back to /play — gate on the permission it
  // actually enforces, not on the role string.
  const canSetUpCompetitions = isSocietyOwner || hasPermission(Permission.CREATE_TOURNAMENT);

  // ── Top-level menu ──────────────────────────────────────────────────────────
  // One button per thing an organiser comes here to do. Each says, in its own
  // description, what lives behind it — so nothing has to be discovered by
  // tapping. Sub-options live on the destination screen, never hidden here.
  const menu = useMemo(() => {
    const items = [];

    if (canCreateRound) {
      items.push({
        id: 'start_round',
        label: 'Start Round',
        desc: 'Pick a course and players, then go straight to scoring',
        icon: <Golf size={24} weight="duotone" />,
        route: '/tournaments/add?mode=quick',
        accent: 'mi-play',
        needsSociety: true,
      });
    }

    items.push(canSetUpCompetitions ? {
      id: 'competitions',
      label: 'Setup a Competition',
      desc: 'New competitions and roll-ups, plus every existing event',
      icon: <Trophy size={24} weight="duotone" />,
      route: '/tournaments',
      accent: 'mi-comps',
      needsSociety: true,
    } : {
      id: 'competitions',
      label: 'My Competitions',
      desc: 'Events you have played or signed up for',
      icon: <Trophy size={24} weight="duotone" />,
      route: '/tournaments?mine=true',
      accent: 'mi-comps',
      needsSociety: true,
    });

    items.push({
      id: 'oom',
      label: 'Order of Merit',
      desc: caps.canManageOOM
        ? 'Set up season-long rankings and see the standings'
        : 'Season-long rankings and standings',
      icon: <Medal size={24} weight="duotone" />,
      route: caps.canManageOOM ? '/order-of-merit/manage' : '/order-of-merit',
      accent: 'mi-oom',
      needsSociety: true,
    });

    items.push({
      id: 'society',
      label: caps.canManageSocieties ? 'Manage the Society' : 'The Society',
      desc: caps.canManageSocieties
        ? 'Members and roles, player profiles, and the audit log'
        : 'See who is in the society and browse player profiles',
      icon: <ShieldChevron size={24} weight="duotone" />,
      route: '/society',
      accent: 'mi-society',
      // Stays enabled with no active society: switching to (or creating) one
      // lives behind this button, so disabling it would be a dead end.
      needsSociety: false,
    });

    if (caps.canManageCourses) {
      items.push({
        id: 'courses',
        label: 'Manage Courses',
        desc: 'Add and edit courses, tees, pars and stroke indexes',
        icon: <Flag size={24} weight="duotone" />,
        route: '/courses',
        accent: 'mi-courses',
      });
    }

    if (caps.canManageHandicap) {
      items.push({
        id: 'handicap',
        label: 'Manage Handicap System',
        desc: 'Set your own cuts and boosts to keep every competition close',
        icon: <Gauge size={24} weight="duotone" />,
        route: '/handicap-adjustments',
        accent: 'mi-handicap',
        needsSociety: true,
      });
    }

    return items;
  }, [caps, canCreateRound, canSetUpCompetitions]);

  const platformItems = caps.canAccessSystemAdmin ? [
    {
      id: 'admin', label: 'Platform Admin',
      desc: 'Users, societies and plans across the platform',
      icon: <Wrench size={24} weight="duotone" />, route: '/admin', accent: 'mi-platform',
    },
    {
      id: 'downloader', label: 'Course Downloader',
      desc: 'Import courses into the global directory',
      icon: <CloudArrowDown size={24} weight="duotone" />, route: '/course-downloader', accent: 'mi-platform',
    },
  ] : [];

  const renderItem = useCallback((item) => {
    const disabled = item.needsSociety && !currentSociety;
    return (
      <Link
        key={item.id}
        to={disabled ? '#' : item.route}
        className={`cc-item ${item.accent}${disabled ? ' disabled' : ''}`}
        onClick={disabled ? (e) => e.preventDefault() : undefined}
      >
        <span className="cc-item-icon">{item.icon}</span>
        <span className="cc-item-text">
          <span className="cc-item-label">{item.label}</span>
          <span className="cc-item-desc">{item.desc}</span>
        </span>
        <FiChevronRight className="cc-item-chevron" size={20} aria-hidden="true" />
      </Link>
    );
  }, [currentSociety]);

  const bottomNavItems = [
    { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} />, needsSociety: true },
    { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} />, needsSociety: true },
    { name: 'Play Golf', route: '/play', icon: <GiGolfTee size={24} />, needsSociety: true },
    { name: 'Manage', route: '/setup', icon: <FiSettings size={24} />, active: true },
  ];

  return (
    <div className="setup-container">
      {/* Manage is a bottom-nav root: no parent to go back to, so the header's
          left slot carries the app drawer instead. Every screen below this one
          has a back arrow there and no drawer. */}
      <MainMenu />
      <PageHeader title="Manage" subtitle={currentSociety?.name || 'No society selected'} />

      <div className="cc-scroll-area">
        {!currentSociety && (
          <div className="cc-empty-note">
            No society selected — open <strong>The Society</strong> below to pick,
            join or create one.
          </div>
        )}

        <div className="cc-menu">{menu.map(renderItem)}</div>

        {platformItems.length > 0 && (
          <div className="cc-menu cc-menu-platform">
            <div className="cc-label">Platform</div>
            {platformItems.map(renderItem)}
          </div>
        )}
      </div>

      <BottomNav menuItems={bottomNavItems} />
    </div>
  );
};

export default ControlCentre;
